import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { collegesAPI } from '../services/api';
import MetricsRow from '../components/dashboard/MetricsRow';
import NextBestActions from '../components/dashboard/NextBestActions';
import TopCollegeMatches from '../components/dashboard/TopCollegeMatches';
import ApplicationPreview from '../components/dashboard/ApplicationPreview';
import ScholarshipsToFocus from '../components/dashboard/ScholarshipsToFocus';
import SignalsFeed from '../components/dashboard/SignalsFeed';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const SkeletonBlock = ({ className }) => (
  <div className={`bg-white rounded-xl border border-gray-100 animate-pulse ${className}`} />
);

const LoadingSkeleton = () => (
  <div className="p-8 space-y-8">
    <div className="space-y-2">
      <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
      <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((n) => <SkeletonBlock key={n} className="h-24" />)}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 space-y-6">
        <SkeletonBlock className="h-52" />
        <SkeletonBlock className="h-64" />
        <SkeletonBlock className="h-52" />
      </div>
      <div className="lg:col-span-2 space-y-6">
        <SkeletonBlock className="h-52" />
        <SkeletonBlock className="h-64" />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [savedColleges, setSavedColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    collegesAPI
      .getSavedColleges()
      .then((data) => setSavedColleges(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error('Failed to load saved colleges:', err);
        setError('Unable to load your dashboard. Please refresh the page.');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]" data-testid="dashboard-error">
        <div className="text-center">
          <p className="text-sm font-medium mb-4" style={{ color: '#374151' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm font-medium px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: '#1a5d3a' }}
            data-testid="dashboard-refresh-btn"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  const greeting = getGreeting();
  const firstName = user?.first_name || null;

  return (
    <div className="p-8 space-y-8" data-testid="dashboard">

      {/* Top greeting */}
      <div>
        <h1
          className="text-3xl font-bold mb-1"
          style={{ color: '#1a1a1a' }}
          data-testid="dashboard-greeting"
        >
          {firstName ? `${greeting}, ${firstName}` : 'Welcome back'}
        </h1>
        <p className="text-sm" style={{ color: '#6B7280' }}>
          Here&apos;s where you stand today.
        </p>
      </div>

      {/* Full-width metrics row */}
      <MetricsRow user={user} savedCollegesCount={savedColleges.length} />

      {/* Two-column cockpit */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* Left — 60% (3 of 5 columns) */}
        <div className="lg:col-span-3 space-y-6">
          <SignalsFeed />
          <TopCollegeMatches />
          <ApplicationPreview />
        </div>

        {/* Right — 40% (2 of 5 columns) */}
        <div className="lg:col-span-2 space-y-6">
          <NextBestActions />
          <ScholarshipsToFocus />
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
